import { MoreVert } from '@mui/icons-material';
import { Menu as MuiMenu } from '@mui/material';
import { useMemo, type ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDataSource } from '~/api/data-sources';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { DataSourcesList__DataSource as DataSource } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  dataSource: DataSource;
  Icon?: ElementType;
}

export const Menu = ({
  buttonType = 'icon',
  dataSource,
  Icon = MoreVert,
  ...cellProps
}: MenuProps) => {
  const { t } = useTranslation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<DataSource>();
  const dataSourceActionProps = mergeGridActionProps(moreTableActionProps(dataSource), cellProps);
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteDataSource] = useDeleteDataSource();

  const { canDestroy, destroyReasons } = useMemo(
    () => ({
      canDestroy: dataSource.canDestroy.value,
      destroyReasons: dataSource.canDestroy.reasons?.fullMessages,
    }),
    [dataSource],
  );

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('pages.data_sources.list.columns.actions_tooltip')}
          onClick={dataSourceActionProps.onClick}
          size="small"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...dataSourceActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        <MoreDeleteAction
          {...moreActionProps}
          disabled={!canDestroy}
          tooltip={destroyReasons?.join()}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            await deleteDataSource({
              variables: { input: { id: moreMenuProps.context.id } },
            });
            moreMenuProps.onClose();
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.data_sources.list.menu.delete_confirm')}
        deleteConfirm
        prompt={t('pages.data_sources.list.menu.delete_prompt')}
        title={t('pages.data_sources.list.menu.delete_title')}
      />
    </>
  );
};
